import { useEffect, useState, type ReactNode } from 'react';

import '../../styles/legal-document.css';
import { LegalBreadcrumb } from './LegalBreadcrumb';
import { LegalThemeToggle, type LegalTheme } from './LegalThemeToggle';

const THEME_KEY = 'nm-share-theme';

interface LegalDocPageProps {
  /** h1 + page title. */
  title: string;
  /** Breadcrumb current-page label (matches the /trust LegalDocCard title). */
  breadcrumbLabel: string;
  /** Small line inline-right of the h1, e.g. "Privacy Notice · v3.0 · 28 April 2026". */
  metaLine?: string;
  /** Lead paragraph under the title. */
  subtitle?: string;
  /** Optional <DocWarning> rendered between the subtitle and the body. */
  warning?: ReactNode;
  /** Widen the reading column (e.g. the sub-processors register's wide table). */
  wide?: boolean;
  /** The document body — TOC + clauses. */
  children: ReactNode;
}

function readStoredTheme(): LegalTheme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem(THEME_KEY);
  return stored === 'dark' ? 'dark' : 'light';
}

/**
 * Wrapper for the 7 legal pages now served as SPA routes. The site Header/Footer
 * come from the root Layout; this component owns the breadcrumb strip, the
 * theme toggle, the title/meta row, and the document body column.
 *
 * data-theme is scoped to the .legal-doc wrapper so the toggle themes only the
 * document panel — it never repaints the rest of the site (see legal-document.css).
 */
export function LegalDocPage({
  title,
  breadcrumbLabel,
  metaLine,
  subtitle,
  warning,
  wide = false,
  children,
}: LegalDocPageProps) {
  const [theme, setTheme] = useState<LegalTheme>('light');

  // Sync stored preference after mount (default light avoids a dark flash on first paint).
  useEffect(() => {
    setTheme(readStoredTheme());
  }, []);

  const toggleTheme = () => {
    setTheme((current) => {
      const next: LegalTheme = current === 'dark' ? 'light' : 'dark';
      window.localStorage.setItem(THEME_KEY, next);
      return next;
    });
  };

  return (
    <div className="legal-doc" data-theme={theme}>
      <div className={wide ? 'legal-doc__col legal-doc__col--wide' : 'legal-doc__col'}>
        <div className="legal-chrome">
          <LegalBreadcrumb label={breadcrumbLabel} />
          <LegalThemeToggle theme={theme} onToggle={toggleTheme} />
        </div>

        <div className="document">
          <div className="doc-title-row">
            <h1 className="doc-title">{title}</h1>
            {metaLine ? <span className="doc-meta-line">{metaLine}</span> : null}
          </div>
          {subtitle ? <p className="doc-subtitle">{subtitle}</p> : null}
          {warning}
          {children}
        </div>
      </div>
    </div>
  );
}
